'use client'

import * as React from 'react'

import { cn } from '@/lib/utils.ts'
import { Button } from '@/app/common/components/button/Button'
import { Calendar } from '@/shad/ui/calendar.tsx'
import { Popover, PopoverContent, PopoverTrigger } from '@/shad/ui/popover.tsx'
import { CalendarIcon } from '@/common/icons/general.tsx'
import { IconType } from '@/common/icons/types.ts'

export interface DatePickerProps {
  name: string // Added prop for the name of the date picker
  readonly?: boolean
  onDateChange: (date: Date | undefined) => void // Updated prop signature
  initialDate?: Date // New prop for initial date selection
  minDate?: Date
  maxDate?: Date
  allowFutureDates?: boolean
  side?: 'top' | 'right' | 'bottom' | 'left'
}

export function DatePicker({
  onDateChange,
  initialDate,
  readonly,
  minDate,
  maxDate,
  allowFutureDates,
  side,
}: DatePickerProps) {
  const [date, setDate] = React.useState<Date | undefined>(initialDate)

  const handleDateSelect = (selectedDate: Date | undefined) => {
    setDate(selectedDate)
    onDateChange(selectedDate)
  }

  const today = new Date()
  const effectiveMaxDate = allowFutureDates
    ? (maxDate ?? undefined)
    : (maxDate ?? today)
  const effectiveMinDate = minDate

  const getDaySuffix = (day: number) => {
    if (day > 3 && day < 21) return 'th'
    switch (day % 10) {
      case 1:
        return 'st'
      case 2:
        return 'nd'
      case 3:
        return 'rd'
      default:
        return 'th'
    }
  }

  const getMonthName = (monthIndex: number) => {
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ]
    return monthNames[monthIndex]
  }

  const formatUTC = (date2: Date) => {
    const year = date2.getUTCFullYear()
    const month = getMonthName(date2.getUTCMonth())
    const day = date2.getUTCDate()
    const daySuffix = getDaySuffix(day)

    return `${month} ${day}${daySuffix}, ${year}`
  }

  React.useEffect(() => {
    setDate(initialDate)
  }, [initialDate])

  return (
    <Popover>
      <PopoverTrigger asChild disabled={readonly}>
        <Button
          variant="outline"
          className={cn(
            'justify-start text-left font-normal w-full',
            !date && 'text-muted-foreground'
          )}
        >
          <CalendarIcon type={IconType.Outline} className="text-lg pr-4" />
          {/* {date ? format(date, 'PPP') : <span>Pick a date</span>} */}
          {date ? formatUTC(date) : <span>Pick a date</span>}
        </Button>
      </PopoverTrigger>

      <PopoverContent side={side} className="w-auto p-0 z-[100]">
        <Calendar
          mode="single"
          fromDate={effectiveMinDate}
          toDate={effectiveMaxDate}
          defaultMonth={
            date
              ? new Date(date.getTime() + date.getTimezoneOffset() * 60000)
              : undefined
          }
          selected={
            date
              ? new Date(date.getTime() + date.getTimezoneOffset() * 60000)
              : date
          }
          onSelect={handleDateSelect}
          initialFocus
          disabled={(dateToCheck) => {
            if (readonly) return true
            if (minDate && dateToCheck < minDate) return true
            if (!allowFutureDates && maxDate && dateToCheck > maxDate)
              return true
            return false
          }}
        />
      </PopoverContent>
    </Popover>
  )
}

// eslint-disable-next-line
export function formatDateToYMD(date: Date | string | '') {
  if (!date) return undefined
  const d = new Date(date)
  return d.toISOString().split('T')[0] // Get the date part (YYYY-MM-DD)
}

export function GetCurrentFormattedDate(date = new Date()) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0') // Months are 0-based
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

export function ConvertStringToDate(date: string | Date | undefined | null) {
  if (typeof date === 'string') {
    const parsedDate = new Date(date)
    // Only set if it's a valid date
    if (!isNaN(parsedDate.getTime())) {
      return parsedDate
    }
    return undefined // Return undefined if the date string is invalid
  } else if (date instanceof Date) {
    return date
  }
  return undefined // Return undefined if date is null or undefined
}
